import { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';

import Yup from '../../utils/yup';
import itemTypeService from '../../service/itemType.service';
import itemTypePropertyService from '../../service/itemTypeProperty.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import { FormBase, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import { cleanValues } from '../../utils/helper';
import ItemTypeFields from './ItemTypeFields';
import ItemTypeReports from './ItemTypeReports';

const defaultFormValues = {
  id: null,
  name: '',
  active: true,
  externalCode: '',
  itemTypePropertyIds: [],
};

const getItemTypePropertyIds = (selectedValue) => {
  if (Array.isArray(selectedValue?.itemTypePropertyIds)) {
    return selectedValue.itemTypePropertyIds;
  }

  if (Array.isArray(selectedValue?.itemTypePropertyLink)) {
    return selectedValue.itemTypePropertyLink
      .map((item) => item.itemTypePropertyId ?? item.itemTypeProperty?.id)
      .filter((id) => id !== null && id !== undefined);
  }

  return [];
};

const ItemTypePage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonVisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [selectedValues, setSelectedValues] = useState({});
  const [initValues, setInitValues] = useState(defaultFormValues);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autocompleteOptions, setAutocompleteOptions] = useState({
    itemTypeProperty: [],
  });

  useEffect(() => {
    getMenus();
    loadAutocompleteOptions();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('itemType');
    setCanEdit(getCanEdit(submenuId));
  }, [menus, getCanEdit, getMenuSubMenuId]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultFormValues, selectedValues)) {
        setInitValues({
          id: selectedValues.id ?? null,
          name: selectedValues.name ?? '',
          active: selectedValues.active ?? true,
          externalCode: selectedValues.externalCode ?? '',
          itemTypePropertyIds: getItemTypePropertyIds(selectedValues),
        });
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultFormValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultFormValues);
    }
  }, [selectedValues]);

  const loadAutocompleteOptions = () => {
    itemTypePropertyService.getData().then((data) => {
      const rows = Array.isArray(data) ? data : data?.rows ?? [];
      setAutocompleteOptions({
        itemTypeProperty: rows.map((row) => ({
          value: row.id,
          label: row.name ?? '',
          name: row.name ?? '',
          externalCode: row.externalCode ?? '',
          dataType: row.dataType ?? '',
        })),
      });
    });
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    active: Yup.boolean(),
    externalCode: Yup.string().nullable(),
    itemTypePropertyIds: Yup.array().of(Yup.number()).nullable(),
  });

  const refreshSelection = async (data) => {
    const selectedId = data?.id ?? initValues.id;
    if (!selectedId) return;
    const detail = await itemTypeService.getItem(selectedId);
    setSelectedValues(detail);
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      itemTypeService.updateItem(cleanValues(values)).then(async (data) => {
        addNotification({
          content: 'Sikeres módosítás!',
          type: NotificationType.SUCCESS,
        });
        await refreshSelection(data);
        setRefetchTable(true);
        setEditButtonVisible(true);
        setViewField(true);
        loadAutocompleteOptions();
      });
    } else if (!addField) {
      itemTypeService.createItem(cleanValues(values)).then(async (data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        await refreshSelection(data);
        setRowSelectionModel(data.id ? [data.id] : []);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
        loadAutocompleteOptions();
      });
    }
  };

  return (
    <FormBase
      name="ItemType"
      leftPanelContent={
        <div className="h-full overflow-auto custom-scrollbar">
          <FormHeaderUI
            addClick={() => {
              setSelectedValues(defaultFormValues);
              setViewField(false);
              setAddField(false);
            }}
            addDisabled={!viewField || !canEdit}
            modClick={() => {
              setViewField(false);
              setEditButtonVisible(false);
            }}
            modDisabled={!viewField || operatorsDisabled || !canEdit}
            copyClick={() => {
              setSelectedValues({
                ...initValues,
                id: null,
              });
              setViewField(false);
              setAddField(false);
            }}
            copyDisabled={!viewField || operatorsDisabled || !canEdit}
          />

          <Formik
            initialValues={initValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ resetForm }) => (
              <Form>
                <ItemTypeFields viewField={viewField} autocompleteOptions={autocompleteOptions} />

                <FormButtonPanelUI
                  onCancelClick={() => {
                    setViewField(true);
                    setEditButtonVisible(true);
                    setAddField(true);
                    resetForm();
                  }}
                  disabled={editButtonVisible && addField}
                />
              </Form>
            )}
          </Formik>
        </div>
      }
      rightPanelContent={
        <ItemTypeReports
          refetch={{ refetchTable, setRefetchTable }}
          values={{ selectedValues, setSelectedValues }}
          viewField={viewField}
          operatorsDisabled={operatorsDisabled}
          rowSelection={{ rowSelectionModel, setRowSelectionModel }}
        />
      }
    />
  );
};

export default ItemTypePage;
